import { BLOCKS, Document } from '@contentful/rich-text-types';

import { ApiMethods, ApiTags, appAPI } from '.';

interface ContentfulAppsResponse {
  items: Array<any>;
  includes: {
    Asset: Array<any>;
    Entry: Array<any>;
  };
}

export enum ContentfulAppType {
  Internal = 'Internal',
  Client = 'Client',
}

export enum ContentfulAppCreator {
  Copilot = 'Copilot',
  Partner = 'Partner',
}

export enum AppType {
  EMBED = 'Embed',
  APP = 'App',
  CORE_APP = 'Core app',
  MARKETPLACE_APP = 'Marketplace app',
  // Value currently used by Contentful template entries.
  APP_TEMPLATE = 'App Template',
  TEMPLATE = 'Template',
}

/** Trim and lowercase so stray Contentful authoring whitespace does not misclassify. */
export const normalizeAppType = (
  appType: string | AppType | undefined,
): string => (appType ?? '').trim().toLowerCase();

export const isEmbedAppType = (
  appType: string | AppType | undefined,
): boolean => normalizeAppType(appType) === AppType.EMBED.toLowerCase();

/**
 * One flat shape for every `appType`, because Contentful has no per-type content
 * model — `partnerApps` holds core modules, marketplace apps, embeds and templates
 * alike. Fields that apply to only some types are annotated below rather than split
 * into a discriminated union: `appType` is unvalidated free text in Contentful (hence
 * `normalizeAppType`), and a union over an untrusted discriminant would have to decide
 * what to do with an entry that fails to narrow. Dropping a card is a worse failure
 * than rendering a blank field.
 *
 * `NOT IN CONTENTFUL` marks a field `transformResponse` reads that does not exist on
 * the content model, so it is always `undefined` for every app. Don't build on one —
 * see POR-21867.
 */
export interface ContentfulApp {
  name: string; // long, descriptive marketing name for Add-app surfaces — NOT the in-product name; Contentful also carries a shorter `defaultProductName` for sidebar/in-product placements (not plumbed through this transform yet)
  description: string; // description of the app
  iconUrl?: string; // url of the icon
  productIcon: string; // url of the in-product icon (new Browse flow); empty string when unset
  appsType: ContentfulAppType; // will this app be displayed internal or client side
  appCreator: ContentfulAppCreator; // NOT IN CONTENTFUL — "Made by" badge reads this and always gets undefined; `madeByAssembly` is the real field
  learnMoreLink: string; // NOT IN CONTENTFUL (raw `setupInstructionsLink`)
  copilotAppId: string; // core modules only — the module id; unset on marketplace apps, embeds and templates
  isFeatured: boolean;
  isPlatformApp: boolean; // Core vs Marketplace discriminator; being retired in favour of `appType` (POR-21861)
  slug: string;
  releaseSegment?: 'Launchpad' | 'Dogfood'; // staged-rollout gate, checked against enable-alpha-apps / enable-beta-apps; unset ⇒ GA
  hideProduct?: boolean; // explicit in-product hide, separate axis from releaseSegment. Absent ⇒ undefined, never assume a default
  presetName?: string; // NOT IN CONTENTFUL
  guideSlug: string;
  pricing: string;
  appType: AppType; // free text in Contentful — compare via normalizeAppType / isEmbedAppType, never ===
  // Template categories (Contentful `categories` multi-select); used by the
  // Browse-apps modal's category filter. Optional — absent for non-template apps.
  categories?: string[];
  appOverview?: Document;
  guideContent: {
    content: Document['content'];
  };
  aboutAppInfo: {
    builtBy: string;
    launchDate: string; // NOT IN CONTENTFUL
    pricing: string;
    visibility: string;
    appType: string;
    website: string;
    requirements?: string;
  };
  appThumbnails: string[];
}

// Rich-text documents reference assets (e.g. inline images) by sys.id only; the
// actual asset fields live in `includes.Asset`. We resolve them onto each
// embedded-asset node so the rich-text renderer can read `target.fields.file.url`.
const resolveEmbeddedAssets = (
  document: Document | undefined,
  assets: Array<any>,
): Document | undefined => {
  if (!document) return undefined;
  return {
    ...document,
    content: document.content.map((node) => {
      if (node.nodeType !== BLOCKS.EMBEDDED_ASSET) return node;
      const assetId = node.data?.target?.sys?.id;
      const asset = assets.find((item) => item.sys.id === assetId);
      return {
        ...node,
        data: {
          ...node.data,
          target: { ...node.data.target, fields: asset?.fields },
        },
      };
    }),
  };
};

export const contentfulApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getContentfulApps: build.query<ContentfulApp[], void>({
      query: () => ({
        path: '/data/contentful',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            select: 'fields',
            contentType: 'partnerApps',
          },
        },
      }),
      providesTags: [ApiTags.contentfulApps],
      // Formats the api response, and renames as it goes — so the field name you read
      // downstream is often NOT the id you query in Contentful:
      //
      //   icon                  -> iconUrl
      //   imageList             -> appThumbnails
      //   setupInstructionsLink -> learnMoreLink
      //   guideArticle          -> guideSlug + guideContent
      //
      // Everything else keeps its Contentful id. Renaming these in Contentful itself is
      // not an option: the marketing site queries the raw ids over GraphQL.
      //
      // `aboutAppInfo` is a partial re-projection rather than a rename: `pricing` and
      // `appType` appear both at the top level and inside it, and `appsType` appears at
      // the top level AND again as `aboutAppInfo.visibility`. So `visibility` has no raw
      // Contentful field of its own — searching the content model for it finds nothing.
      //
      // The destructure below is a FIXED list. A field absent from it never reaches
      // consumers no matter how well populated it is in Contentful, and it fails silently
      // as `undefined` rather than as an error — which is the usual explanation for "the
      // field is set but my component sees nothing". Adding a field means touching three
      // places: this destructure, the returned object, and the ContentfulApp interface.
      //
      // Contentful cannot change a field's type in place — a type change appears as a NEW
      // field id, with the old field left behind. `category` (Symbol) was superseded by
      // `categories` (multi-select) this way; the old field is still present, now named
      // "Category (remove this)", so reading the old id type-checks and quietly returns
      // stale data. When a field seems to have changed shape, check the live content model
      // for a sibling id before trusting the one you already know about.
      transformResponse(baseQueryReturnValue: ContentfulAppsResponse) {
        if (!baseQueryReturnValue) return [];

        const allApps = baseQueryReturnValue.items; // all apps added in Contentful CMS
        const allAssets = baseQueryReturnValue.includes.Asset; // all asset files
        const allEntries = baseQueryReturnValue.includes.Entry; // entries contains all the referenced info related to the app
        return allApps.map((app) => {
          const {
            name,
            appCreator,
            appsType,
            appType,
            description,
            isFeatured,
            setupInstructionsLink,
            copilotAppId,
            icon,
            productIcon: productIconAsset,
            isPlatformApp,
            releaseSegment,
            hideProduct,
            presetName,
            guideArticle,
            slug,
            builtBy,
            launchDate,
            pricing,
            imageList,
            website,
            requirements,
            appOverview,
            categories,
          } = app.fields;

          const appThumbnails = (imageList || []).map(
            (image: { sys: { id: string } }) => {
              const thumbnailInfo = allAssets.find(
                (asset) => asset.sys.id === image?.sys.id,
              );
              return thumbnailInfo?.fields?.file?.url || '';
            },
          );

          // get icon information from all assets array
          const iconInfo = allAssets.find(
            (asset) => asset.sys.id === icon?.sys.id,
          );
          // url to access the app icon
          const iconUrl = iconInfo?.fields?.file?.url || '';

          // url to access the in-product icon used by the new Browse flow
          const productIconInfo = allAssets.find(
            (asset) => asset.sys.id === productIconAsset?.sys.id,
          );
          const productIcon = productIconInfo?.fields?.file?.url || '';

          const { slug: guideSlug, content: guideContent } =
            allEntries.find((entry) => entry.sys.id === guideArticle?.sys?.id)
              ?.fields || {};

          return {
            name,
            description,
            iconUrl,
            productIcon,
            appsType,
            appCreator,
            learnMoreLink: setupInstructionsLink,
            copilotAppId,
            isFeatured,
            isPlatformApp,
            releaseSegment,
            hideProduct,
            presetName,
            guideSlug,
            guideContent,
            slug,
            pricing,
            appType,
            categories,
            appThumbnails,
            appOverview: resolveEmbeddedAssets(appOverview, allAssets),
            aboutAppInfo: {
              builtBy,
              launchDate,
              pricing,
              appType,
              visibility: appsType,
              website,
              requirements,
            },
          };
        });
      },
    }),
  }),
});

export const { useGetContentfulAppsQuery } = contentfulApi;
